import {
  CloseButton,
  Combobox,
  ComboboxItem,
  ComboboxOptionProps,
  ComboboxProps,
  InputBase,
  Loader,
  TextInputProps,
  useCombobox,
} from "@mantine/core";
import { useIntersection } from "@mantine/hooks";
import { FC, ReactNode, useEffect, useMemo, useRef } from "react";
import styles from "../select.module.scss";

interface Data {
  value: string;
  label: string;
}

interface RenderOption extends Data {
  [key: string]: any;
}

interface InfiniteScrollSelectProps {
  data: Data[];
  renderOption?: (item: RenderOption) => ReactNode;
  loading?: boolean;
  onBottomReached?: () => void;
  onChange: (value: string) => void;
  mah?: number | string;
  textInputProps?: TextInputProps;
  containerProps?: ComboboxProps;
  optionProps?: ComboboxOptionProps;
  error?: string | boolean;
  onBlur?: () => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  value: ComboboxItem | null;
  children?: ReactNode;
  searchable?: boolean;
  placeholder?: string;
  disabled?: boolean;

  width?: number;
  searchValue: string;
  onSearchChange: (value: string) => void;
  icon?: ReactNode;
  clearable?: boolean;
  onClear?: () => void;

  isSelectable?: (item: Data) => boolean;
  onInvalidSelect?: (item: Data) => void;
}

export const AsyncSearchableSelect: FC<InfiniteScrollSelectProps> = ({
  data = [],
  loading = false,
  onBottomReached,
  onChange,
  mah = 200,
  textInputProps = {},
  containerProps = {},
  optionProps = {},
  error,
  width,
  searchValue,
  onSearchChange,
  placeholder,
  icon,
  clearable = false,
  onClear,
  value,
  disabled,
  renderOption,
  isSelectable,
  onInvalidSelect,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { ref, entry } = useIntersection({
    root: containerRef.current,
    threshold: 1,
  });

  const combobox = useCombobox({
    onDropdownOpen: () => {
      combobox.focusSearchInput();
    },
    onDropdownClose: () => {
      combobox.focusTarget();
    },
  });

  useEffect(() => {
    if (entry?.isIntersecting && onBottomReached && !loading) {
      onBottomReached();
    }
  }, [entry?.isIntersecting, onBottomReached, loading]);

  const optionsList: ReactNode = useMemo(() => {
    return (
      <>
        {data.map((item, index) => (
          <Combobox.Option
            ref={index === data.length - 1 ? ref : null}
            key={`${item.value}-${index}`}
            onClick={() => {
              if (!isSelectable || isSelectable(item)) {
                onChange(item.value);
              } else {
                onInvalidSelect?.(item);
              }

              combobox.closeDropdown();
            }}
            fz={12}
            {...optionProps}
            value={item.value}
          >
            {renderOption?.(item) ?? item.label}
          </Combobox.Option>
        ))}
      </>
    );
  }, [data]);

  return (
    <Combobox
      store={combobox}
      withinPortal={false}
      onOptionSubmit={() => {
        combobox.closeDropdown();
        onSearchChange("");
      }}
      {...containerProps}
    >
      <Combobox.Target>
        <InputBase
          readOnly
          placeholder={placeholder}
          value={value?.label ?? ""}
          size="md"
          error={error}
          classNames={{
            root: styles.root,
            wrapper: styles.wrapper,
            input: styles.input,
            error: styles.error_text,
          }}
          disabled={disabled}
          leftSection={icon}
          style={{ width: width }}
          onClick={() => combobox.toggleDropdown()}
          rightSection={
            clearable && value ? (
              <CloseButton
                size="sm"
                onMouseDown={(event) => event.preventDefault()}
                onClick={onClear}
                aria-label="Clear value"
              />
            ) : (
              <Combobox.Chevron />
            )
          }
          {...textInputProps}
        />
      </Combobox.Target>

      <Combobox.Dropdown
        mah={mah}
        ref={containerRef}
        style={{ overflowY: "auto", border: "1px solid black" }}
      >
        <Combobox.Search
          value={searchValue}
          size="md"
          onChange={(event) => {
            onSearchChange(event.currentTarget.value);
          }}
        />
        {data.length > 0 ? (
          optionsList
        ) : (
          <div style={{ padding: "10px", textAlign: "center" }}>No data</div>
        )}
        <div style={{ textAlign: "center" }}>
          {loading && <Loader size="sm" />}
        </div>
      </Combobox.Dropdown>
    </Combobox>
  );
};
